import { AnalyticsHandler } from '@Foundation/analyticsHandler';
import { eventBus } from '@Foundation/eventbus/eventbus';
import { types } from './constants';

const {
  ANSWER,
  BACK,
  CHECK_IF_FINAL,
  FILTER_ANSWERS,
  FILTER_STEPS,
  INIT_QUIZ,
  INIT_RESULTS_PAGE,
  NAVIGATE,
  REMOVE_ANSWERED_QUESTIONS,
  RESET_SELECTIONS,
  NEXT,
  SEND_NEXT_EVENT,
  SEND_RESULTS,
  SET_ACTIVE_QUESTION,
  SET_ANSWERS,
  SET_ERROR,
  SET_LOADING_RESULTS_STATE,
  SET_NEWSLETTER_FORM_DISPLAY,
  SET_STEPS,
  SET_QUESTIONS,
  SET_QUIZ_CONFIG,
  START,
  UNSELECT_ANSWERS,
  UPDATE_MULTI_SELECTION,
  UPDATE_NEWSLETTER_FORM_DISPLAY,
  UPDATE_SINGLE_SELECTION,
  VALIDATE_DECISION_TREE,
  VALIDATE_MIN_OPTIONS_SELECTED,
} = types;

export const state = {
  activeQuestion: null,
  answers: [],
  apiUrl: null,
  changeQuestionsOnSelection: false,
  currentStepIndex: 0,
  final: false,
  isLoadingResults: false,
  newsletterActive: false,
  optionalQuestionsPending: [],
  redirectAfterSendResults: true,
  showNewsletterForm: false,
  started: false,
  steps: [],
  tooltipCommon: {},
  questionList: [],
  questionDelayMilliseconds: null,
  resultsPage: null,
  requiredError: false,
};

export const mutations = {
  [CHECK_IF_FINAL](state) {
    state.final = state.activeQuestion.isLastQuestion;
  },
  [FILTER_ANSWERS](state, cb) {
    state.answers = state.answers.filter(cb);
  },
  [FILTER_STEPS](state, cb) {
    state.steps = state.steps.filter(cb);
  },
  [RESET_SELECTIONS](state) {
    state.activeQuestion.selections.map(selection => {
      selection.selected = false;
      return selection;
    });
  },
  [SET_ACTIVE_QUESTION](state, question) {
    state.activeQuestion = question;
    state.currentStepIndex = state.steps.findIndex(step => step.id === question.id);
  },
  [SET_ANSWERS](state) {
    const answers = [];
    state.questionList.forEach(question => {
      const selectedIds = [];
      question.selections.forEach(selection => {
        if (selection.selected) {
          selectedIds.push({
            selectionId: selection.id,
            priority: selection.index || null,
          });
        }
      });

      if (selectedIds.length === 0) {
        return;
      }

      answers.push({
        questionId: question.id,
        selectedIds: selectedIds,
      });
    });
    state.answers = answers;
  },
  [SET_LOADING_RESULTS_STATE](state, status) {
    state.isLoadingResults = status;
  },
  [SET_NEWSLETTER_FORM_DISPLAY](state, status) {
    state.showNewsletterForm = status;

    setTimeout(() => {
      document.querySelectorAll('.skincare-newsletter .input-text')[0].focus();
    }, 100);
  },
  [SET_STEPS](state) {
    const requiredSteps = state.questionList.filter(question => question.isRequired);

    state.steps = requiredSteps.map(question => {
      return { id: question.id, title: question.title };
    });
  },
  [SET_QUESTIONS](state, questions) {
    state.questionList = questions.map((question) => ({
      ...question,
      hasOptionalQuestion: question.selections.some(selection => selection.hasOptionalQuestion),
    }));
  },
  [SET_QUIZ_CONFIG](state, config) {
    Object.assign(state, config);
  },
  [UPDATE_MULTI_SELECTION](state, { id, skip }) {
    const { max } = state.activeQuestion;
    const numberOfSelectedOptions = state.activeQuestion.selections.filter(
      selection => selection.selected
    ).length;
    const limitReached = max ? max === numberOfSelectedOptions : false;
    const currentSelection = state.activeQuestion.selections.find(selection => selection.id === id);

    if (skip) {
      state.activeQuestion.counter = 0;
      state.activeQuestion.limitReached = false;
      state.activeQuestion.selections.forEach((selection) => {
        selection.index = null;
        selection.selected = selection.skip && !selection.selected;
      });

      return;
    }

    if (limitReached && !currentSelection.selected) {
      return;
    }

    state.activeQuestion.selections.map(selection => {
      if (selection.id === id) {
        selection.selected = !selection.selected;

        if (selection.selected && state.activeQuestion.showCounter) {
          selection.index = state.activeQuestion.counter + 1;
          state.activeQuestion.counter++;
        } else if (!selection.selected && state.activeQuestion.showCounter) {
          const previousSecondPriority = state.activeQuestion.selections.find(
            selection => selection.index === 2
          );
          if (previousSecondPriority) {
            previousSecondPriority.index = 1;
          }

          selection.index = null;
          state.activeQuestion.counter--;
        }
      }
      return selection;
    });

    state.activeQuestion.limitReached = max
      ? max === state.activeQuestion.selections.filter(selection => selection.selected).length
      : false;
  },
  [UPDATE_SINGLE_SELECTION](state, id) {
    state.activeQuestion.selections.map(selection => {
      if (selection.id === id) {
        selection.selected = !selection.selected;
      }
      return selection;
    });
  },
  [VALIDATE_MIN_OPTIONS_SELECTED](state) {
    if (state.activeQuestion.selections.some(({ selected, skip }) => selected && skip)) {
      state.activeQuestion.validated = true;
      state.requiredError = false;
      return;
    }

    const min = state.activeQuestion.min || 1;
    state.activeQuestion.validated =
      state.activeQuestion.selections.filter(selection => selection.selected).length >= min;

    if (state.activeQuestion.validated) {
      state.requiredError = false;
    }
  },
  [SET_ERROR](state, payload) {
    state.requiredError = payload;
  },
};

export const actions = {
  [ANSWER]({ commit, dispatch }) {
    dispatch(VALIDATE_DECISION_TREE);
    commit(SET_ANSWERS);
  },
  [BACK]({ commit, state }) {
    const currentStepIndex = state.steps.findIndex(step => step.id === state.activeQuestion.id);
    const previousStep = state.steps[currentStepIndex - 1];
    const previousQuestion = state.questionList.find(question => question.id === previousStep.id);

    commit(SET_ACTIVE_QUESTION, previousQuestion);
    commit(CHECK_IF_FINAL);
  },
  [INIT_QUIZ]({ commit }, { questions, config }) {
    commit(SET_QUIZ_CONFIG, config);
    commit(SET_QUESTIONS, questions);
    commit(SET_STEPS);
  },
  [INIT_RESULTS_PAGE]({ commit }, { config }) {
    commit(SET_QUIZ_CONFIG, config);
  },
  [NAVIGATE]({ commit, dispatch, getters, state }, questionId) {
    if (getters.activeQuestion.hasOptionalQuestion) {
      const optionalQuestionSelections = getters.activeQuestion.selections.filter(
        ({ hasOptionalQuestion }) => hasOptionalQuestion
      );
      const optionalQuestionSelectionsIds = optionalQuestionSelections.map(
        (selection) => selection.nextQuestionId
      );
      const optionalQuestionSelected = optionalQuestionSelections.find(({ selected }) => selected);
      const optionalQuestionSelectedData =
        optionalQuestionSelected &&
        getters.questionList.find(({ id }) => id === optionalQuestionSelected.nextQuestionId);
      const isOptionalQuestionAnswered =
        optionalQuestionSelectedData &&
        optionalQuestionSelectedData.validated &&
        optionalQuestionSelectedData.selections.some(({ selected }) => selected);

      // Case: The user rolls back, selects an optional trigger, and tries to jump to an advanced question.
      // The operation should be intercepted, pointing the user to the optional question triggered.
      const shouldAskOptional =
        getters.isOptionalQuestionSelected &&
        (getters.isUpdatingAnswer || !isOptionalQuestionAnswered);

      // Case: The user rolls back, unselects the optional trigger, and tries to select the previous optional question dot (that will be removed).
      // The operation should be intercepted, pointing the user to the next valid question.
      const shouldBypassOptional =
        !getters.isOptionalQuestionSelected &&
        getters.isUpdatingAnswer &&
        optionalQuestionSelectionsIds.includes(questionId);

      // Dispatching the Next event will handle the exceptions
      if (shouldAskOptional || shouldBypassOptional) {
        dispatch(NEXT);
        return;
      }

      if (getters.isUpdatingAnswer) {
        dispatch(VALIDATE_DECISION_TREE);
      }
    }

    const nextQuestion = state.questionList.find(question => question.id === questionId);
    commit(SET_ACTIVE_QUESTION, nextQuestion);
    commit(CHECK_IF_FINAL);
  },
  [REMOVE_ANSWERED_QUESTIONS]({ commit, dispatch }, questions) {
    commit(FILTER_ANSWERS, (answer) => !questions.some((id) => id === answer.questionId));
    commit(FILTER_STEPS, (step) => !questions.some((id) => id === step.id));
    dispatch(UNSELECT_ANSWERS, questions);
  },
  [SEND_NEXT_EVENT]({ getters, state }) {
    const { activeQuestion } = state;
    const removeExtraSpaces = (str) => str.replace(/\s+/g, ' ');
    const removeHtmlTags = (html) => html.replace(/<.*?>/g, '');
    const removeLineBreaks = (str) => str.replace(/[\r\n]/gm, '');
    const formatText = str => removeExtraSpaces(removeLineBreaks(str.toLowerCase()));
    const selectedAnswers = activeQuestion.selections.filter(selection => selection.selected);
    const selectedAnswersLabels = selectedAnswers.map(selection => selection.label);

    const questionDescription = formatText(removeHtmlTags(activeQuestion.title));
    const selections = formatText(selectedAnswersLabels.join('::'));
    const stepNumber = getters.currentStepNumber;

    AnalyticsHandler.pushDataLayer({
      ecommerce: undefined,
      event: 'uaevent',
      eventAction: `${stepNumber}::${questionDescription}`,
      eventCategory: 'skincare finder',
      event_detail: `${stepNumber}::${questionDescription}`,
      eventLabel: selections,
      event_name: 'skincare_finder',
      question_entity: selections,
    });
  },
  [SEND_RESULTS]({ commit, state }, newsletterData) {
    commit(SET_LOADING_RESULTS_STATE, true);

    const body = Object.assign({ questions: state.answers }, newsletterData);

    fetch(state.apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json;charset=utf-8' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    })
      .then((response) => response.json())
      .then((jsonResponse) => {
        if (newsletterData && newsletterData.recaptchaToken && !jsonResponse.recaptchaSuccess) {
          eventBus.$emit('recaptcha-error');
          return;
        }

        if (state.redirectAfterSendResults) {
          window.location.href = state.resultsPage;
        }
      })
      .catch((err) => {
        commit(SET_LOADING_RESULTS_STATE, false);

        /* eslint no-console: ["error", { allow: ["warn", "error"] }] */
        console.error(err);
      });
  },
  [START]({ commit, state }) {
    state.started = true;
    commit(SET_ACTIVE_QUESTION, state.questionList[0]);

    setTimeout(() => {
      document.querySelector('.question__title').focus();
    }, 100);
  },
  [NEXT]({ commit, dispatch }) {
    if (!state.activeQuestion.validated) {
      commit(SET_ERROR, true);
      return;
    }

    const activeQuestionAnswers = state.activeQuestion.selections.filter(
      selection => selection.selected
    );
    const answersWithOptionalQuestion = activeQuestionAnswers.filter(
      answer => answer.hasOptionalQuestion
    );

    const goNext = (customNextQuestion) => {
      const defaultNextQuestion = state.questionList.find(
        question => question.id === state.activeQuestion.defaultNextQuestion
      );
      const nextQuestion = customNextQuestion || defaultNextQuestion;
      const isGoingToRequiredQuestion = nextQuestion.isRequired;

      dispatch(ANSWER);

      // If going to optional question, add it to the steps count
      if (!isGoingToRequiredQuestion) {
        const { id, title } = nextQuestion;
        const questionIndex = state.questionList.findIndex(question => question.id === id);

        if (!state.steps.some((step) => step.id === id)) {
          state.steps.splice(questionIndex, 0, { id, title });
        }
      }

      dispatch(VALIDATE_DECISION_TREE);
      commit(SET_ACTIVE_QUESTION, nextQuestion);

      setTimeout(() => {
        document.querySelector('.question__title').focus();
      }, 100);
    };

    dispatch(SEND_NEXT_EVENT);

    if (answersWithOptionalQuestion.length > 0) {
      answersWithOptionalQuestion.forEach(answer => {
        const optionalQuestion = state.questionList.find(
          question => question.id === answer.nextQuestionId
        );

        if (state.optionalQuestionsPending.find(question => question.id === optionalQuestion.id)) {
          return;
        }

        state.optionalQuestionsPending.push(optionalQuestion);
      });
    }

    if (state.final && state.optionalQuestionsPending.length === 0) {
      dispatch(ANSWER);

      if (state.newsletterActive) {
        commit(SET_NEWSLETTER_FORM_DISPLAY, true);
      } else {
        dispatch(SEND_RESULTS, { subscribe: false });
      }

      return;
    }

    if (state.optionalQuestionsPending.length > 0) {
      goNext(state.optionalQuestionsPending[0]);
      state.optionalQuestionsPending.shift();
      return;
    }

    goNext();
    commit(CHECK_IF_FINAL);
  },
  [UNSELECT_ANSWERS]({ commit }, questions) {
    const treatedQuestionList = state.questionList.map((question) => {
      if (!questions.some((id) => id === question.id)) {
        return question;
      }

      return {
        ...question,
        selections: [
          ...question.selections.map((selection) => ({
            ...selection,
            selected: false,
          })),
        ],
        validated: false,
      };
    });

    commit(SET_QUESTIONS, treatedQuestionList);
  },
  updateSingleSelection({ state, commit, dispatch }, { id }) {
    commit(RESET_SELECTIONS);
    commit(UPDATE_SINGLE_SELECTION, id);
    commit(VALIDATE_MIN_OPTIONS_SELECTED);
    commit(CHECK_IF_FINAL);

    if (!state.final) {
      dispatch(NEXT);
    }
  },
  updateMultiSelection({ commit }, { id, skip }) {
    commit(UPDATE_MULTI_SELECTION, { id, skip });
    commit(VALIDATE_MIN_OPTIONS_SELECTED);
  },
  [UPDATE_NEWSLETTER_FORM_DISPLAY]({ commit }, value) {
    commit(SET_NEWSLETTER_FORM_DISPLAY, value);
  },
  [VALIDATE_DECISION_TREE]({ dispatch, getters }) {
    if (
      !getters.activeQuestion.hasOptionalQuestion ||
      !getters.isUpdatingAnswer ||
      getters.isOptionalQuestionSelected
    ) {
      return;
    }

    const optionalSelectionsIds = getters.activeQuestion.selections
      .filter((selection) => selection.hasOptionalQuestion)
      .map((selection) => selection.nextQuestionId);
    dispatch(REMOVE_ANSWERED_QUESTIONS, optionalSelectionsIds);
  },
};

export const getters = {
  activeQuestion: (state) => state.activeQuestion,
  answers: (state) => state.answers,
  answersCount: (state) => state.answers.length,
  changeQuestionsOnSelection: (state) => state.changeQuestionsOnSelection,
  currentStepNumber: (state) => state.currentStepIndex + 1,
  final: (state) => state.final,
  isOptionalQuestionSelected: (state) =>
    state.activeQuestion.selections.some(
      ({ hasOptionalQuestion, selected }) => hasOptionalQuestion && selected
    ),
  isUpdatingAnswer: (state) => {
    const savedAnswer =
      state.answers.find((answer) => answer.questionId === state.activeQuestion.id) &&
      state.answers
        .find((answer) => answer.questionId === state.activeQuestion.id)
        .selectedIds.map((selection) => selection.selectionId);
    if (!savedAnswer) {
      return false;
    }

    const currentAnswer = state.activeQuestion.selections
      .filter((selection) => selection.selected)
      .map((selection) => selection.id);

    return currentAnswer.sort().toString() !== savedAnswer.sort().toString();
  },
  isLoadingResults: (state) => state.isLoadingResults,
  isSkipSelected: (state) =>
    state.activeQuestion &&
    state.activeQuestion.selections.some(({ selected, skip }) => selected && skip),
  questionList: (state) => state.questionList,
  showNewsletterForm: (state) => state.showNewsletterForm,
  requiredError: (state) => state.requiredError,
  started: (state) => state.started,
  steps: (state) => state.steps,
  tooltipCommon: (state) => state.tooltipCommon,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
